import React from 'react';
import { Box, ButtonProps } from '@mui/material';
import { useFormikContext } from 'formik';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { Button } from '../../../Button';
import { CancelButton, SubmitButton } from '../../../Form';
import { AvailabilityFormValues } from './AvailabilityForm.types';
import { TimeRange } from '@uplift/types';

import isEqual from 'lodash/isEqual';


export type ResetOrRemoveAvailabilityButtonProps = {
  defaultAvailability?: TimeRange[];
  isAddingOverride?: boolean;
  loading?: boolean;
} & Pick<ButtonProps, 'fullWidth' | 'color'>;


export const ResetOrRemoveAvailabilityButton: React.FC<ResetOrRemoveAvailabilityButtonProps> = ({ fullWidth, color, ...props }) => {
  const l10n = useL10n();
  const formikContext = useFormikContext<AvailabilityFormValues>();
  const availabilityEmpty = formikContext.values.availability?.length === 0;

  if ((availabilityEmpty && props.defaultAvailability) || props.isAddingOverride) {
    const defaultTheSameAsCurrent = isEqual(
      formikContext.values.availability.map(s => [ s.start, s.end ]),
      props.defaultAvailability?.map(s => [ s.start, s.end ]) ?? [],
    );

    return (
      <Button
        size="medium"
        fullWidth={fullWidth}
        color={color ?? 'secondary'}
        style={{ whiteSpace: 'nowrap' }}
        disabled={props.loading || defaultTheSameAsCurrent}
        title={defaultTheSameAsCurrent ? 'Default availability is empty' : undefined}
        onClick={() => {
          const newAvailability = (props.defaultAvailability ?? []).map((r, i) => ({ id: Date.now() + i, ...r }));

          formikContext.setValues({ availability: newAvailability });
        }}
      >
        {l10n.calendar.availability.editForm.revertToDefault}
      </Button>
    );
  }

  const disabled = (
    props.loading
      || !formikContext.values.availability.length
  );

  return (
    <Button
      onClick={() => formikContext.setValues({ availability: [] })}
      style={{ whiteSpace: 'nowrap' }}
      disabled={disabled}
      fullWidth={fullWidth}
      color={color ?? 'secondary'}
    >
      {l10n.calendar.availability.editForm.removeAllAvailability}
    </Button>
  );
};


export type AvailabilityFormButtonsProps = {
  handleClose: () => void;
  defaultAvailability?: TimeRange[];
  loading?: boolean;
  isAddingOverride?: boolean;
};


export const AvailabilityFormButtons: React.FC<AvailabilityFormButtonsProps> = (props) => {
  const l10n = useL10n();
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Box display="flex" justifyContent="space-between" width="100%" boxSizing="border-box">
        <SubmitButton fullWidth size="medium" color="secondary">{l10n.general.save}</SubmitButton>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="space-between" width="100%" boxSizing="border-box">
      <Box>
        <ResetOrRemoveAvailabilityButton
          defaultAvailability={props.defaultAvailability}
          isAddingOverride={props.isAddingOverride}
          loading={props.loading}
        />
      </Box>
      <Box display="flex">
        <CancelButton size="medium" onClick={props.handleClose}>{l10n.general.cancel}</CancelButton>
        <SubmitButton size="medium" color="secondary">{l10n.general.save}</SubmitButton>
      </Box>
    </Box>
  );
};
